// ============================================
// PAGE - Detalle Boleta
// ============================================

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBoletaDetalle, useActualizarBoleta, TipoPago, useUIStore } from '@almacenes/core';
import type { DetalleBoletaProducto, BoletaPago } from '@almacenes/core';
import { MdPrint, MdDelete, MdRestore, MdWarning, MdRefresh, MdReceipt, MdCalendarToday, MdPerson, MdDownload } from 'react-icons/md';
import { GiWallet } from "react-icons/gi";
import { FaMoneyBillWave, FaCreditCard, FaExchangeAlt, FaHandHolding } from 'react-icons/fa';
import { SiVisa } from 'react-icons/si';
import { usePrintBoleta } from '../../hooks/usePrintBoleta';
import { totalEsperadoDePagos } from '../../utils/redondeo';

interface DetalleEditable extends DetalleBoletaProducto {
  eliminado: boolean;
  venderPor?: string;
  codigoProductoAlmacen?: string | null;
}

const TIPO_PAGO_MIXTO = 'MIXTO';

// Recalcular una línea con descuento como cantidad × precio perdería el descuento ya aplicado,
// por eso el backend solo permite eliminarla (mismo criterio en la validación del servidor).
const LINEA_CON_DESCUENTO_NO_EDITABLE =
  'Esta línea tiene descuento: no se puede cambiar su cantidad ni su precio. Puedes eliminarla.';

const tieneDescuento = (detalle: DetalleBoletaProducto): boolean => (detalle.descuento ?? 0) !== 0;

export const BoletaDetallePage = () => {
  const { boletaId } = useParams<{ boletaId: string }>();
  const navigate = useNavigate();
  const addToast = useUIStore((state) => state.addToast);
  
  const { data: boleta, isLoading, error } = useBoletaDetalle(Number(boletaId));
  const actualizarBoletaMutation = useActualizarBoleta();
  const { printBoleta, loadingPrintId } = usePrintBoleta();

  // Estados para edición
  const [tipoPagoEditable, setTipoPagoEditable] = useState<string>('');
  const [detallesEditables, setDetallesEditables] = useState<DetalleEditable[]>([]);
  const [paymentsEditables, setPaymentsEditables] = useState<BoletaPago[] | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Estado temporal para inputs de precio (permite borrar completamente)
  const [preciosTemporales, setPreciosTemporales] = useState<Record<number, string>>({});
  // Estados temporales para inputs de monto en payments
  const [montosTemporales, setMontosTemporales] = useState<Record<number, string>>({});

  // Scroll al tope al montar el componente
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, []);

  // Inicializar estados editables cuando carga la boleta
  useEffect(() => {
    if (boleta) {
      setTipoPagoEditable(boleta.tipoPago);
      setDetallesEditables(
        boleta.detalles.map(detalle => ({
          ...detalle,
          eliminado: false,
          venderPor: (detalle as any).venderPor || 'F', // 'U' = Unidad, 'F' = Fracción (default)
          codigoProductoAlmacen: (detalle as any).codigoProductoAlmacen || null,
        }))
      );
      // Inicializar payments si existen
      const payments = boleta.payments;
      if (payments && Array.isArray(payments) && payments.length > 0) {
        setPaymentsEditables(payments);
      } else {
        setPaymentsEditables(null);
      }
    }
  }, [boleta]);

  const handleVolver = () => {
    navigate('/detalle-boleta');
  };

  const handleCantidadChange = (detalleId: number, nuevaCantidad: string, venderPor?: string) => {
    const cantidad = parseFloat(nuevaCantidad);
    
    // No permitir 0 ni valores negativos
    if (isNaN(cantidad) || cantidad <= 0) return;

    // Si es unidad, validar que sea entero
    if (venderPor === 'U' && !Number.isInteger(cantidad)) {
      return;
    }

    setDetallesEditables(prev =>
      prev.map(detalle => {
        if (detalle.id === detalleId && !tieneDescuento(detalle)) {
          const nuevoTotal = cantidad * detalle.precioVenta;
          return {
            ...detalle,
            cantidad,
            total: nuevoTotal,
          };
        }
        return detalle;
      })
    );
  };

  const handlePrecioVentaChange = (detalleId: number, nuevoPrecio: string) => {
    // Guardar el valor temporal (permite borrar completamente)
    setPreciosTemporales(prev => ({
      ...prev,
      [detalleId]: nuevoPrecio
    }));

    // Si está vacío, no actualizar el precio real
    if (nuevoPrecio === '' || nuevoPrecio === '.') {
      return;
    }

    // Remover puntos (separadores de miles) y parsear
    const precioLimpio = nuevoPrecio.replace(/\./g, '');
    const precio = parseInt(precioLimpio);
    
    // Solo actualizar el estado si es un número válido mayor a 0
    if (isNaN(precio) || precio <= 0) return;

    setDetallesEditables(prev =>
      prev.map(detalle => {
        if (detalle.id === detalleId && !tieneDescuento(detalle)) {
          const nuevoTotal = detalle.cantidad * precio;
          return {
            ...detalle,
            precioVenta: precio,
            total: nuevoTotal,
          };
        }
        return detalle;
      })
    );
  };

  const toggleEliminarDetalle = (detalleId: number) => {
    setDetallesEditables(prev =>
      prev.map(detalle =>
        detalle.id === detalleId
          ? { ...detalle, eliminado: !detalle.eliminado }
          : detalle
      )
    );
  };

  const handlePaymentMethodChange = (orden: number, newMethod: string) => {
    if (!paymentsEditables) return;
    setPaymentsEditables(prev =>
      prev!.map(payment =>
        payment.orden === orden
          ? { ...payment, methodPayment: newMethod }
          : payment
      )
    );
  };

  const handlePaymentAmountChange = (orden: number, newAmount: string) => {
    // Guardar el valor temporal
    setMontosTemporales(prev => ({
      ...prev,
      [orden]: newAmount
    }));

    // Si está vacío, no actualizar el monto real
    if (newAmount === '' || newAmount === '.') {
      return;
    }

    // Remover puntos (separadores de miles) y parsear
    const montoLimpio = newAmount.replace(/\./g, '');
    const monto = parseInt(montoLimpio);
    
    // Solo actualizar el estado si es un número válido mayor a 0
    if (isNaN(monto) || monto <= 0) return;

    if (!paymentsEditables) return;
    setPaymentsEditables(prev =>
      prev!.map(payment =>
        payment.orden === orden
          ? { ...payment, amount: monto }
          : payment
      )
    );
  };

  const handleCompletarRestante = (orden: number) => {
    if (!paymentsEditables) return;
    
    const totalProductos = calcularTotalAPagar();
    const totalPagos = paymentsEditables.reduce((sum, p) => sum + p.amount, 0);
    const restante = totalProductos - totalPagos;
    
    // Ajustar el monto tanto si falta dinero (restante > 0) como si sobra (restante < 0)
    if (restante !== 0) {
      setPaymentsEditables(prev =>
        prev!.map(payment =>
          payment.orden === orden
            ? { 
                ...payment, 
                amount: Math.max(0, payment.amount + restante) // Asegura que no sea negativo
              }
            : payment
        )
      );
    }
  };

  const getPaymentIcon = (methodPayment: string) => {
    switch (methodPayment) {
      case TipoPago.EFECTIVO:
        return <FaMoneyBillWave className="text-xl text-green-600" />;
      case TipoPago.DEBITO:
        return <FaCreditCard className="text-xl text-blue-600" />;
      case TipoPago.CREDITO:
        return <SiVisa className="text-xl text-purple-600" />;
      case TipoPago.TRANSFERENCIA:
        return <FaExchangeAlt className="text-xl text-orange-600" />;
      case TipoPago.FIADO:
        return <FaHandHolding className="text-xl text-red-600" />;
      default:
        return <FaMoneyBillWave className="text-xl text-gray-600" />;
    }
  };

  const handleActualizar = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmActualizar = async () => {
    if (!boleta) return;

    // Validar que la suma de pagos múltiples coincida con el total de productos
    if (paymentsEditables && paymentsEditables.length > 0) {
      const totalPagos = paymentsEditables.reduce((sum, p) => sum + p.amount, 0);
      const totalProductos = calcularTotalAPagar();
      const diferencia = Math.abs(totalPagos - totalProductos);

      if (diferencia > 2) {
        addToast(
          'error',
          `La suma de los pagos ($${formatNumero(totalPagos)}) debe ser igual al total de productos ($${formatNumero(totalProductos)})`
        );
        setShowConfirmModal(false);
        return;
      }

      // Validar que los pagos en efectivo cumplan con la ley de redondeo
      const efectivosSinRedondear = paymentsEditables.filter(p => {
        if (p.methodPayment === TipoPago.EFECTIVO) {
          return p.amount % 10 !== 0 && p.amount > 0;
        }
        return false;
      });
      
      if (efectivosSinRedondear.length > 0) {
        const montosInvalidos = efectivosSinRedondear.map(p => `$${formatNumero(p.amount)}`).join(', ');
        addToast('error', `Los montos en efectivo deben cumplir con la ley de redondeo (múltiplos de 10). Montos inválidos: ${montosInvalidos}`);
        setShowConfirmModal(false);
        return;
      }
    }

    try {
      const datos: any = {
        tipoPago: tipoPagoEditable,
        detalles: detallesEditables.map(detalle => {
          const baseDetalle: any = {
            detalleId: detalle.id,
            cantidad: detalle.cantidad,
            eliminado: detalle.eliminado,
          };
          
          // Si es producto manual (sin código de producto), enviar precio de venta
          if (!detalle.codigoProductoAlmacen) {
            baseDetalle.precioVenta = detalle.precioVenta;
          }
          
          return baseDetalle;
        }),
      };

      // Si hay pagos múltiples, incluirlos en los datos
      if (paymentsEditables && paymentsEditables.length > 0) {
        datos.payments = paymentsEditables.map(payment => {
          const paymentData: any = {
            methodPayment: payment.methodPayment,
            amount: payment.amount
          };
          
          // Si es FIADO, incluir fiadoClienteId
          if (payment.methodPayment === TipoPago.FIADO && payment.fiadoClienteId) {
            paymentData.fiadoClienteId = payment.fiadoClienteId;
          }
          
          return paymentData;
        });
      }

      await actualizarBoletaMutation.mutateAsync({
        boletaId: Number(boletaId),
        datos,
      });

      // Cerrar el modal
      setShowConfirmModal(false);

      // Mostrar mensaje de éxito
      addToast('success', 'Boleta actualizada correctamente');

      // Volver a la pantalla anterior
      navigate('/detalle-boleta');
    } catch (error) {
      console.error('Error al actualizar boleta:', error);
      setShowConfirmModal(false);
      const apiError = error as { message?: string };
      addToast('error', apiError?.message || 'Error al actualizar la boleta. Intente nuevamente.');
    }
  };

  const formatFechaHora = (fecha: string) => {
    const date = new Date(fecha);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const formatNumero = (value: number) => {
    return Math.round(value).toLocaleString('es-CL');
  };

  // Calcular totales dinámicamente basados en detalles editables (sin incluir eliminados)
  const calcularTotalActual = () => {
    return detallesEditables
      .filter(detalle => !detalle.eliminado)
      .reduce((sum, detalle) => sum + detalle.total, 0);
  };

  // Total contra el que deben cuadrar los pagos: con pago solo en efectivo es el total de las líneas
  // redondeado por ley (artículo 12); con otros medios de pago, el total exacto.
  const calcularTotalAPagar = () =>
    totalEsperadoDePagos(
      calcularTotalActual(),
      (paymentsEditables ?? []).map(p => p.methodPayment),
      TipoPago.EFECTIVO
    );

  // Descuentos de las líneas que siguen en la venta (el total de cada línea ya viene neto de su descuento)
  const calcularDescuentoActual = () => {
    return detallesEditables
      .filter(detalle => !detalle.eliminado)
      .reduce((sum, detalle) => sum + (detalle.descuento ?? 0), 0);
  };

  const calcularSubtotal = (total: number) => {
    return total / 1.19;
  };

  const calcularIVA = (total: number) => {
    return total - calcularSubtotal(total);
  };

  const handleDownloadExcel = async () => {
    if (!boleta) return;

    const totalActual = calcularTotalActual();
    const subtotal = calcularSubtotal(totalActual);
    const iva = calcularIVA(totalActual);

    const { Workbook } = await import('exceljs');
    const wb = new Workbook();
    wb.creator = 'Almacenes Digitales';
    wb.created = new Date();

    const C = {
      dark:    'FF11194F',
      brand:   'FF43BFFF',
      bgLight: 'FFF3F4F9',
      white:   'FFFFFFFF',
      green:   'FF22C55E',
      border:  'FFE5E7EB',
    };
    const CLP = '#,##0';
    const N = 6;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styleTitle = (row: any) => {
      row.height = 34;
      row.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 13 };
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.brand } };
      row.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
      for (let i = 1; i <= N; i++) row.getCell(i).border = {};
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styleHeader = (row: any) => {
      row.height = 26;
      row.font = { name: 'Calibri', bold: true, color: { argb: C.white }, size: 10 };
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.dark } };
      row.alignment = { horizontal: 'center', vertical: 'middle' };
      for (let i = 1; i <= N; i++) {
        row.getCell(i).border = {
          top:    { style: 'medium', color: { argb: C.brand } },
          bottom: { style: 'medium', color: { argb: C.brand } },
          left:   { style: 'thin',   color: { argb: C.dark } },
          right:  { style: 'thin',   color: { argb: C.dark } },
        };
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const styleData = (row: any, isAlt: boolean) => {
      row.height = 20;
      if (isAlt) row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.bgLight } };
      for (let i = 1; i <= N; i++) {
        row.getCell(i).border = {
          bottom: { style: 'thin', color: { argb: C.border } },
          left:   { style: 'thin', color: { argb: C.border } },
          right:  { style: 'thin', color: { argb: C.border } },
        };
      }
    };

    const ws = wb.addWorksheet('Detalle Boleta');
    ws.columns = [
      { key: 'producto',    width: 38 },
      { key: 'tipo',        width: 14 },
      { key: 'cantidad',    width: 12 },
      { key: 'precioVenta', width: 16 },
      { key: 'descuento',   width: 16 },
      { key: 'total',       width: 16 },
    ];

    const tRow = ws.addRow([`Boleta #${boleta.id}  |  ${formatFechaHora(boleta.fecha)}  |  ${boleta.vendedor}  |  ${tipoPagoEditable}`, '', '', '', '', '']);
    ws.mergeCells('A1:F1');
    styleTitle(tRow);

    const hRow = ws.addRow(['Producto', 'Tipo', 'Cantidad', 'Precio Venta ($)', 'Descuento ($)', 'Total ($)']);
    styleHeader(hRow);

    detallesEditables
      .filter(d => !d.eliminado)
      .forEach((d, idx) => {
        const row = ws.addRow([
          d.nombreProducto,
          d.codigoProductoAlmacen ? 'Inventario' : 'Manual',
          d.cantidad,
          d.precioVenta,
          d.descuento ?? 0,
          d.total,
        ]);
        styleData(row, idx % 2 !== 0);
        for (const col of [4, 5, 6]) {
          row.getCell(col).numFmt = CLP;
          row.getCell(col).alignment = { horizontal: 'right' };
        }
      });

    ws.addRow([]);

    const makeResumenRow = (label: string, value: number, bgArgb: string, fontArgb: string) => {
      const row = ws.addRow(['', '', '', '', label, value]);
      row.height = 24;
      row.font = { name: 'Calibri', bold: true, color: { argb: fontArgb }, size: 11 };
      row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
      row.getCell(5).alignment = { horizontal: 'right' };
      row.getCell(6).numFmt = CLP;
      row.getCell(6).alignment = { horizontal: 'right' };
      for (let i = 5; i <= 6; i++) {
        row.getCell(i).border = {
          top:    { style: 'medium', color: { argb: C.brand } },
          bottom: { style: 'medium', color: { argb: C.brand } },
          left:   { style: 'thin',   color: { argb: C.border } },
          right:  { style: 'thin',   color: { argb: C.border } },
        };
      }
    };

    const descuentoActual = calcularDescuentoActual();
    if (descuentoActual > 0) {
      makeResumenRow('Precio de lista', Math.round(totalActual + descuentoActual), C.bgLight, C.dark);
      makeResumenRow('Descuentos', -Math.round(descuentoActual), C.bgLight, C.dark);
    }
    makeResumenRow('Valor Neto', Math.round(subtotal), C.bgLight, C.dark);
    makeResumenRow('IVA (19%)', Math.round(iva), C.bgLight, C.dark);
    makeResumenRow('TOTAL', Math.round(totalActual), C.green, C.white);

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `boleta_${boleta.id}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="px-4 md:px-6 lg:px-8 pb-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Cargando detalle de boleta...</div>
        </div>
      </div>
    );
  }

  if (error || !boleta) {
    return (
      <div className="px-4 md:px-6 lg:px-8 pb-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">Error al cargar la boleta</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="px-4 md:px-6 lg:px-3 pb-28">

        {/* Card con información general de la boleta */}
      <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm border border-gray-100 mt-3">
        {/* Información general - estilo imagen en una sola fila distribuida */}
        <div className="flex items-center justify-between gap-8 mb-6 overflow-x-auto pb-2">
          
          {/* Boleta */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="bg-blue-100 rounded-lg p-2.5">
              <MdReceipt className="text-2xl text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Boleta</p>
              <p className="text-base font-bold text-blue-600">
                #{boleta.id}
              </p>
            </div>
          </div>

          {/* Fecha */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="bg-blue-100 rounded-lg p-2.5">
              <MdCalendarToday className="text-2xl text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Fecha</p>
              <p className="text-sm font-bold text-[#11194F] whitespace-nowrap">
                {formatFechaHora(boleta.fecha)}
              </p>
            </div>
          </div>

          {/* Vendedor */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="bg-blue-100 rounded-lg p-2.5">
              <MdPerson className="text-2xl text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Vendedor</p>
              <p className="text-sm font-bold text-[#11194F]">
                {boleta.vendedor}
              </p>
            </div>
          </div>

          {/* Tipo de Pago */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="bg-purple-100 rounded-lg p-2.5">
              <FaCreditCard className="text-2xl text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Tipo de pago</p>
              {paymentsEditables && paymentsEditables.length > 0 ? (
                <p className="text-sm font-bold text-purple-700">
                  {/* El backend guarda una fila de pago incluso con un solo medio: solo es MIXTO si hay más de uno */}
                  {paymentsEditables.length > 1 ? TIPO_PAGO_MIXTO : paymentsEditables[0].methodPayment}
                </p>
              ) : (
                <select
                  value={tipoPagoEditable}
                  onChange={(e) => setTipoPagoEditable(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-xs font-semibold text-[#11194F] focus:outline-none focus:ring-2 focus:ring-[#43BFFF]"
                >
                  <option value={TipoPago.EFECTIVO}>{TipoPago.EFECTIVO}</option>
                  <option value={TipoPago.DEBITO}>{TipoPago.DEBITO}</option>
                  <option value={TipoPago.CREDITO}>{TipoPago.CREDITO}</option>
                  <option value={TipoPago.TRANSFERENCIA}>{TipoPago.TRANSFERENCIA}</option>
                </select>
              )}
            </div>
          </div>
          
        </div>
      </div>

      {/* Layout de dos columnas: Productos/Pagos | Resumen */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Columna izquierda - Productos y Pagos Múltiples */}
        <div className="lg:col-span-3 space-y-6">
          
          {/* Card de Productos */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 mb-4">
              <div className="text-[#43BFFF]">
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#11194F]">Productos</h2>
                <p className="text-sm text-gray-500">Edita las cantidades o elimina productos</p>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                <tr style={{ backgroundColor: '#F3F4F9' }}>
                  <th className="px-6 py-4 text-left text-sm font-bold" style={{ color: '#11194F' }}>
                    Producto
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                    Cantidad
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                    Precio Venta
                  </th>
                  <th className="px-6 py-4 text-right text-sm font-bold" style={{ color: '#11194F' }}>
                    Total
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-bold" style={{ color: '#11194F' }}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {detallesEditables.map((detalle, index) => {
                  const esProductoManual = !detalle.codigoProductoAlmacen;
                  const lineaConDescuento = tieneDescuento(detalle);
                  return (
                    <tr
                      key={detalle.id}
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${
                        detalle.eliminado ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          {detalle.nombreProducto}
                          {detalle.eliminado && (
                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                              Se eliminará
                            </span>
                          )}
                        </div>
                        {(detalle.descuento ?? 0) > 0 && (
                          <p className="text-xs font-medium text-amber-600" title={LINEA_CON_DESCUENTO_NO_EDITABLE}>
                            Descuento: -${formatNumero(detalle.descuento ?? 0)}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {esProductoManual || lineaConDescuento ? (
                          <span
                            className="text-sm text-gray-900"
                            title={lineaConDescuento ? LINEA_CON_DESCUENTO_NO_EDITABLE : undefined}
                          >
                            {detalle.cantidad}
                          </span>
                        ) : (
                          <input
                            type="number"
                            step={detalle.venderPor === 'U' ? '1' : '0.01'}
                            min={detalle.venderPor === 'U' ? '1' : '0.01'}
                            value={detalle.cantidad}
                            onChange={(e) => handleCantidadChange(detalle.id, e.target.value, detalle.venderPor)}
                            disabled={detalle.eliminado}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#43BFFF] disabled:bg-gray-100"
                            title={detalle.venderPor === 'U' ? 'Solo números enteros' : 'Permite decimales'}
                          />
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {esProductoManual && !lineaConDescuento ? (
                          <input
                            type="text"
                            value={
                              detalle.id in preciosTemporales
                                ? preciosTemporales[detalle.id]
                                : formatNumero(detalle.precioVenta)
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              // Permitir solo números y puntos
                              const cleanValue = value.replace(/[^\d.]/g, '');
                              handlePrecioVentaChange(detalle.id, cleanValue);
                            }}
                            onFocus={() => {
                              // Al hacer foco, mostrar el valor sin formato
                              setPreciosTemporales(prev => ({
                                ...prev,
                                [detalle.id]: detalle.precioVenta.toString()
                              }));
                            }}
                            onBlur={() => {
                              // Al perder el foco, limpiar el valor temporal
                              setPreciosTemporales(prev => {
                                const newState = { ...prev };
                                delete newState[detalle.id];
                                return newState;
                              });
                            }}
                            disabled={detalle.eliminado}
                            className="w-28 px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#43BFFF] disabled:bg-gray-100"
                            title="Solo números enteros"
                            placeholder="0"
                          />
                        ) : (
                          <span className="text-sm text-gray-900">
                            ${formatNumero(detalle.precioVenta)}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-right font-semibold text-gray-900">
                        ${formatNumero(detalle.total)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => toggleEliminarDetalle(detalle.id)}
                          className="p-2 rounded-lg transition-all hover:scale-110"
                          style={{ color: detalle.eliminado ? '#22C55E' : '#EF4444' }}
                          title={detalle.eliminado ? 'Restaurar' : 'Eliminar'}
                        >
                          {detalle.eliminado ? (
                            <MdRestore className="text-xl" />
                          ) : (
                            <MdDelete className="text-xl" />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card de Pagos Múltiples (si aplica) */}
        {paymentsEditables && paymentsEditables.length > 0 && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <div className="mb-6">
              <div className='flex items-center gap-2 mb-4'>                
                <GiWallet className="text-2xl text-purple-600" />                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-[#11194F]">Pagos Múltiples</h2>
                  </div>
                  <p className="text-sm text-gray-500 mb-4">Distribuye los pagos en los diferentes métodos</p>
                </div>
              </div>
              
              
              {/* Lista de pagos con franjas separadoras */}
              <div className="mb-4">
                {paymentsEditables
                  .sort((a, b) => a.orden - b.orden)
                  .map((payment, index, array) => {
                    const esFiado = payment.methodPayment === TipoPago.FIADO;
                    const isLast = index === array.length - 1;
                    return (
                      <div
                        key={payment.orden}
                        className={`flex items-center gap-3 bg-white p-4 ${!isLast ? 'border-b border-gray-200' : ''}`}
                      >
                        {/* Icono del método de pago */}
                        <div className="flex-shrink-0">
                          {getPaymentIcon(payment.methodPayment)}
                        </div>
                        
                        {/* Selector de método de pago */}
                        <div className="flex-1 min-w-0">
                          {esFiado ? (
                            <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg">
                              <span className="text-sm font-semibold text-gray-700">{TipoPago.FIADO}</span>
                            </div>
                          ) : (
                            <select
                              value={payment.methodPayment}
                              onChange={(e) => handlePaymentMethodChange(payment.orden, e.target.value)}
                              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg text-sm font-semibold text-[#11194F] focus:outline-none focus:ring-2 focus:ring-[#43BFFF] focus:border-[#43BFFF]"
                            >
                              <option value={TipoPago.EFECTIVO}>{TipoPago.EFECTIVO}</option>
                              <option value={TipoPago.DEBITO}>{TipoPago.DEBITO}</option>
                              <option value={TipoPago.CREDITO}>{TipoPago.CREDITO}</option>
                              <option value={TipoPago.TRANSFERENCIA}>{TipoPago.TRANSFERENCIA}</option>
                            </select>
                          )}
                        </div>
                        
                        {/* Input de monto con $ adelante */}
                        <div className="w-40 flex-shrink-0">
                          {esFiado ? (
                            <div className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg">
                              <span className="text-sm font-bold text-gray-700">
                                ${formatNumero(payment.amount)}
                              </span>
                            </div>
                          ) : (
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                                $
                              </span>
                              <input
                                type="text"
                                value={
                                  payment.orden in montosTemporales
                                    ? montosTemporales[payment.orden]
                                    : formatNumero(payment.amount)
                                }
                                onChange={(e) => {
                                  const value = e.target.value;
                                  const cleanValue = value.replace(/[^\d.]/g, '');
                                  handlePaymentAmountChange(payment.orden, cleanValue);
                                }}
                                onFocus={(e) => {
                                  e.target.select();
                                  setMontosTemporales(prev => ({
                                    ...prev,
                                    [payment.orden]: payment.amount.toString()
                                  }));
                                }}
                                onBlur={() => {
                                  setMontosTemporales(prev => {
                                    const newState = { ...prev };
                                    delete newState[payment.orden];
                                    return newState;
                                  });
                                }}
                                className={`w-full pl-7 pr-3 py-2 border-2 rounded-lg text-sm text-right font-bold focus:outline-none focus:ring-2 ${
                                  payment.methodPayment === TipoPago.EFECTIVO && 
                                  payment.amount % 10 !== 0 && 
                                  payment.amount > 0
                                    ? 'border-red-500 focus:border-red-600 focus:ring-red-600'
                                    : 'border-gray-300 focus:border-[#43BFFF] focus:ring-[#43BFFF]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                          )}
                        </div>

                        {/* Botón Completar restante */}
                        {!esFiado && (
                          <button
                            onClick={() => handleCompletarRestante(payment.orden)}
                            className="px-4 py-2 bg-white text-[#43BFFF] border-2 border-[#43BFFF] text-sm font-medium rounded-lg hover:bg-blue-50 transition-colors whitespace-nowrap"
                          >
                            Ajustar diferencia
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>

            </div>
          </div>
        )}
      </div>

      {/* Columna derecha - Resumen de la venta (sticky) */}
      <div className="lg:col-span-1">
        <div className="lg:sticky lg:top-6">
          {/* Card Resumen de la venta */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-lg lg:text-xl font-bold text-[#11194F] mb-4">Resumen de la venta</h2>
              <div className="space-y-3">
              {calcularDescuentoActual() > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-xs lg:text-sm font-medium text-gray-700">Precio de lista</span>
                    <span className="text-base lg:text-lg font-bold text-gray-900">
                      ${formatNumero(calcularTotalActual() + calcularDescuentoActual())}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs lg:text-sm font-medium text-gray-700">Descuentos</span>
                    <span className="text-base lg:text-lg font-bold text-amber-600">
                      -${formatNumero(calcularDescuentoActual())}
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center">
                <span className="text-xs lg:text-sm font-medium text-gray-700">Subtotal Neto</span>
                <span className="text-base lg:text-lg font-bold text-gray-900">
                  ${formatNumero(calcularSubtotal(calcularTotalActual()))}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs lg:text-sm font-medium text-gray-700">IVA (19%)</span>
                <span className="text-base lg:text-lg font-bold text-gray-900">
                  ${formatNumero(calcularIVA(calcularTotalActual()))}
                </span>
              </div>
              <div className="border-t-2 border-blue-200 pt-3 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-base lg:text-lg font-bold text-[#11194F]">TOTAL</span>
                  <span className="text-xl lg:text-2xl font-bold text-[#11194F]">
                    ${formatNumero(calcularTotalActual())}
                  </span>
                </div>
                {calcularTotalAPagar() !== calcularTotalActual() && (
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs text-gray-500">
                      Total en efectivo (redondeado por ley artículo 12)
                    </span>
                    <span className="text-base lg:text-lg font-bold text-[#11194F]">
                      ${formatNumero(calcularTotalAPagar())}
                    </span>
                  </div>
                )}
              </div>

              {/* Si hay pagos múltiples, mostrar pagado y diferencia */}
              {paymentsEditables && paymentsEditables.length > 0 && (
                <>
                  <div className="border-t-2 border-blue-200 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs lg:text-sm font-medium text-gray-700">Pagado</span>
                      <span className="text-xl lg:text-2xl font-bold text-green-600">
                        ${formatNumero(paymentsEditables.reduce((sum, p) => sum + p.amount, 0))}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs lg:text-sm font-medium text-gray-700">Diferencia</span>
                    <div className="text-right">
                      {(() => {
                        const diferencia = paymentsEditables.reduce((sum, p) => sum + p.amount, 0) - calcularTotalAPagar();
                        const diferenciaMayorA2 = Math.abs(diferencia) > 2;
                        
                        return (
                          <>
                            <p className={`text-xl lg:text-2xl font-bold ${
                              diferenciaMayorA2 
                                ? (diferencia < 0 ? 'text-red-600' : 'text-orange-600')
                                : 'text-green-600'
                            }`}>
                              ${formatNumero(Math.abs(diferencia))}
                            </p>
                            {diferencia < -2 && (
                              <p className="text-xs text-red-600 font-medium">Falta por pagar</p>
                            )}
                            {diferencia > 2 && (
                              <p className="text-xs text-orange-600 font-medium">Sobra dinero</p>
                            )}
                            {Math.abs(diferencia) <= 2 && (
                              <p className="text-xs text-green-600 font-medium">Cuadrado</p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {/* Mensaje de advertencia */}
                  {(() => {
                    const diferencia = paymentsEditables.reduce((sum, p) => sum + p.amount, 0) - calcularTotalAPagar();
                    if (Math.abs(diferencia) <= 2) return null;
                    
                    return (
                      <div className={`flex items-start gap-2 rounded-lg p-3 mt-3 ${
                        diferencia < 0 
                          ? 'bg-red-50 border border-red-200' 
                          : 'bg-orange-50 border border-orange-200'
                      }`}>
                        <MdWarning className={`text-lg flex-shrink-0 mt-0.5 ${
                          diferencia < 0 ? 'text-red-600' : 'text-orange-600'
                        }`} />
                        <p className={`text-sm ${
                          diferencia < 0 ? 'text-red-700' : 'text-orange-700'
                        }`}>
                          {diferencia < 0 
                            ? 'Aún falta dinero por asignar en los pagos.'
                            : 'Hay dinero de más asignado. Ajusta los montos de los pagos.'}
                        </p>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Card de Consejo para Pagos Mixtos */}
          {paymentsEditables && paymentsEditables.length > 0 && (
            <div className="bg-blue-50 rounded-2xl p-5 shadow-sm border-2 border-blue-200 mt-6">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="text-base font-bold text-blue-900 mb-2">
                    💡 Pagos Mixtos
                  </h3>
                  <div className="text-sm text-blue-800 space-y-2">
                    <p>
                      La <strong>diferencia</strong> indica si los pagos cuadran con el total:
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li><strong className="text-green-700">Verde</strong>: Los pagos cuadran perfectamente.</li>
                      <li><strong className="text-red-700">Rojo</strong>: Falta dinero por asignar en los medios de pago.</li>
                      <li><strong className="text-orange-700">Naranja</strong>: Hay dinero de más asignado.</li>
                    </ul>
                    <p className="mt-2 pt-2 border-t border-blue-300">
                      <strong>Consejo:</strong> Usa el botón <span className="px-2 py-1 bg-white text-blue-600 rounded border border-blue-400 font-semibold text-xs">Ajustar diferencia</span> para distribuir automáticamente el monto faltante o sobrante.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </div>

    {/* Footer fijo con botones de acción */}
    <div className="fixed bottom-0 left-0 lg:left-16 right-0 bg-white border-t border-gray-200 shadow-lg z-40">
      <div className="px-3 md:px-6 lg:px-8 py-3 md:py-4">
        {/* Layout para móvil: Grid de 2x2 */}
        <div className="md:hidden grid grid-cols-2 gap-2 max-w-7xl mx-auto">
          {/* Primera fila: Cancelar (ocupa 2 columnas) */}
          <button
            onClick={handleVolver}
            className="col-span-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 text-sm"
            title="Cancelar y volver"
          >
            Cancelar
          </button>

          {/* Segunda fila: 3 botones de acción */}
          <button
            onClick={handleDownloadExcel}
            className="px-3 py-2.5 bg-[#22C55E] text-white rounded-lg font-medium hover:bg-[#16A34A] transition-colors flex items-center justify-center gap-1 text-xs"
            title="Descargar Excel"
          >
            <MdDownload className="text-lg" />
            <span className="hidden xs:inline">Excel</span>
          </button>
          
          <button
            onClick={() => printBoleta(boleta.id)}
            disabled={loadingPrintId !== null}
            className={`px-3 py-2.5 text-black rounded-lg font-medium transition-colors flex items-center justify-center gap-1 text-xs ${
              loadingPrintId === boleta.id
                ? 'bg-gray-400 cursor-wait'
                : 'bg-[#FACC15] hover:bg-[#EAB308]'
            } disabled:opacity-70 disabled:cursor-not-allowed`}
            title={loadingPrintId === boleta.id ? 'Imprimiendo...' : 'Imprimir'}
          >
            {loadingPrintId === boleta.id ? (
              <>
                <MdRefresh className="text-lg animate-spin" />
                <span className="hidden xs:inline">...</span>
              </>
            ) : (
              <>
                <MdPrint className="text-lg" />
                <span className="hidden xs:inline">Imprimir</span>
              </>
            )}
          </button>
          
          <button
            onClick={handleActualizar}
            disabled={actualizarBoletaMutation.isPending}
            className="col-span-2 px-4 py-2.5 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm"
          >
            {actualizarBoletaMutation.isPending ? 'Actualizando...' : 'Actualizar'}
          </button>
        </div>

        {/* Layout para tablet/desktop: Horizontal */}
        <div className="hidden md:flex gap-3 justify-between items-center max-w-7xl mx-auto">
          {/* Botón Cancelar - Izquierda */}
          <button
            onClick={handleVolver}
            className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            title="Cancelar y volver"
          >
            Cancelar
          </button>

          {/* Botones de acción agrupados - Derecha */}
          <div className="flex gap-3">
            <button
              onClick={handleDownloadExcel}
              className="px-6 py-3 bg-[#22C55E] text-white rounded-lg font-medium hover:bg-[#16A34A] transition-colors flex items-center gap-2"
              title="Descargar Excel"
            >
              <MdDownload className="text-xl" />
              Descargar Excel
            </button>
            
            <button
              onClick={() => printBoleta(boleta.id)}
              disabled={loadingPrintId !== null}
              className={`px-6 py-3 text-black rounded-lg font-medium transition-colors flex items-center gap-2 ${
                loadingPrintId === boleta.id
                  ? 'bg-gray-400 cursor-wait'
                  : 'bg-[#FACC15] hover:bg-[#EAB308]'
              } disabled:opacity-70 disabled:cursor-not-allowed`}
              title={loadingPrintId === boleta.id ? 'Imprimiendo...' : 'Imprimir'}
            >
              {loadingPrintId === boleta.id ? (
                <>
                  <MdRefresh className="text-xl animate-spin" />
                  Imprimiendo...
                </>
              ) : (
                <>
                  <MdPrint className="text-xl" />
                  Imprimir
                </>
              )}
            </button>
            
            <button
              onClick={handleActualizar}
              disabled={actualizarBoletaMutation.isPending}
              className="px-6 py-3 bg-[#43BFFF] text-white rounded-lg font-medium hover:bg-[#3AABEB] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {actualizarBoletaMutation.isPending ? 'Actualizando...' : 'Actualizar'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Modal de confirmación */}
    {showConfirmModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
          <div className="flex flex-col items-center text-center">
              <div className="mb-4">
                <MdWarning
                  className="text-6xl"
                  style={{ color: boleta.dteEmitido ? '#FB923C' : '#43BFFF' }}
                />
              </div>
              
              <h3 className="text-xl font-bold text-[#11194F] mb-2">
                {boleta.dteEmitido ? 'Advertencia: DTE Emitido' : 'Confirmar Actualización'}
              </h3>
              
              {boleta.dteEmitido ? (
                <div className="text-gray-600 mb-6 space-y-2">
                  <p className="font-medium">
                    Está modificando una venta que ya fue emitida como Documento Tributario Electrónico (DTE).
                  </p>
                  <p className="text-sm">
                    Cualquier cambio debe regularizarse previamente ante el SII.
                  </p>
                  <p className="font-semibold">
                    ¿Confirma que desea continuar?
                  </p>
                </div>
              ) : (
                <p className="text-gray-600 mb-6">
                  ¿Está seguro que desea actualizar esta boleta?
                </p>
              )}

              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  disabled={actualizarBoletaMutation.isPending}
                  className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmActualizar}
                  disabled={actualizarBoletaMutation.isPending}
                  className="flex-1 px-4 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: boleta.dteEmitido ? '#FB923C' : '#22C55E',
                  }}
                >
                  {actualizarBoletaMutation.isPending ? 'Actualizando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
